import React from 'react';
import {
  Box, Button, Checkbox, InputAdornment, MenuItem, MenuList, Stack, TextField, Typography,
} from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';

export interface OptionItem {
  value: string;
  /** Shown instead of the value when given. */
  label?: string;
  /** How many records carry this value; shown on the right. */
  count?: number | null;
  /**
   * A second, smaller line under the label: a person's job title ("Case
   * Manager II"), say. Search matches it too.
   */
  detail?: string | null;
  /**
   * Shown but not changeable: it keeps whatever state `value` gives it, and
   * "Select all" and "Clear" leave it alone. For a choice that is always on,
   * such as a role that sees everything.
   */
  disabled?: boolean;
}

export interface OptionListProps {
  options: OptionItem[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Show a search box. Defaults to on when there are more than 8 options. */
  searchable?: boolean;
  searchPlaceholder?: string;
  loading?: boolean;
  /** Height before the list scrolls, in px. */
  maxHeight?: number;
  /** Focus the search box, or the first option, on mount. */
  autoFocus?: boolean;
}

/**
 * A checkbox list for picking several values, with counts and search.
 *
 * "Select all" and "Clear" act on what the search is showing, so you can type
 * "north" and take every match in one click. What was already picked when the
 * list opened is listed first.
 *
 * Used inside `FilterSelect`; use it directly in any popover or panel that
 * needs a multi-select. For one value from a short list, use a Select.
 */
export function OptionList({
  options,
  value,
  onChange,
  searchable,
  searchPlaceholder = 'Search',
  loading,
  maxHeight = 280,
  autoFocus,
}: OptionListProps) {
  const [query, setQuery] = React.useState('');
  const showSearch = searchable ?? options.length > 8;
  const selected = React.useMemo(() => new Set(value), [value]);

  // What was picked when the list opened goes first, so a filter showing
  // "+3" shows those three without scrolling. Taken once, on opening: sorting
  // again on every tick would move the row just clicked out from under the
  // pointer. A picked value missing from the options — no records in the
  // current range — is still listed, or it could not be unticked.
  const [pickedAtOpen] = React.useState(() => new Set(value));
  const ordered = React.useMemo(() => {
    const known = new Set(options.map((o) => o.value));
    const missing: OptionItem[] = Array.from(pickedAtOpen)
      .filter((v) => !known.has(v))
      .map((v) => ({ value: v, count: null }));
    return [
      ...options.filter((o) => pickedAtOpen.has(o.value)),
      ...missing,
      ...options.filter((o) => !pickedAtOpen.has(o.value)),
    ];
  }, [options, pickedAtOpen]);

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? ordered.filter((o) => `${o.label ?? o.value} ${o.detail ?? ''}`.toLowerCase().includes(needle))
    : ordered;

  const locked = React.useMemo(
    () => new Set(options.filter((o) => o.disabled).map((o) => o.value)),
    [options],
  );
  const toggle = (item: string) => {
    if (locked.has(item)) return;
    onChange(selected.has(item) ? value.filter((v) => v !== item) : [...value, item]);
  };
  const selectVisible = () =>
    onChange(Array.from(new Set([
      ...value, ...visible.filter((o) => !o.disabled).map((o) => o.value),
    ])));
  const clearVisible = () => {
    const shown = new Set(visible.map((o) => o.value));
    onChange(value.filter((v) => locked.has(v) || (needle ? !shown.has(v) : false)));
  };

  return (
    <Stack>
      {showSearch && (
        <Box sx={{ px: 1, pt: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus={autoFocus}
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      )}

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1.5, pt: 0.75 }}>
        <Typography
          variant="caption"
          sx={(t) => ({ flex: 1, color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}
        >
          {value.length ? `${value.length} selected` : `${options.length} options`}
        </Typography>
        <Button size="small" onClick={selectVisible} disabled={!visible.length}>
          Select all
        </Button>
        <Button size="small" onClick={clearVisible} disabled={!value.some((v) => !locked.has(v))}>
          Clear
        </Button>
      </Stack>

      <MenuList
        dense
        autoFocusItem={autoFocus && !showSearch}
        aria-multiselectable
        sx={{ maxHeight, overflowY: 'auto', py: 0.5 }}
      >
        {loading && !options.length ? (
          <MenuItem disabled>Loading…</MenuItem>
        ) : visible.length === 0 ? (
          <MenuItem disabled>No matches</MenuItem>
        ) : (
          visible.map((option) => {
            const checked = selected.has(option.value);
            return (
              <MenuItem
                key={option.value}
                role="menuitemcheckbox"
                aria-checked={checked}
                disabled={option.disabled}
                onClick={() => toggle(option.value)}
                sx={{ gap: 1 }}
              >
                <Checkbox size="small" checked={checked} disabled={option.disabled}
                  tabIndex={-1} disableRipple sx={{ p: 0 }} />
                {option.detail ? (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{option.label ?? option.value}</Typography>
                    <Typography variant="caption" component="div"
                      sx={(t) => ({ color: t.tokens.text.tertiary, whiteSpace: 'normal' })}>
                      {option.detail}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {option.label ?? option.value}
                  </Typography>
                )}
                {option.count != null && (
                  <Typography
                    variant="caption"
                    sx={(t) => ({ color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}
                  >
                    {option.count.toLocaleString('en-US')}
                  </Typography>
                )}
              </MenuItem>
            );
          })
        )}
      </MenuList>
    </Stack>
  );
}
